"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import moment from "moment-timezone";
import "moment/locale/fr";
import { Link } from "@/i18n/navigation";
import AddComment from "./AddComment/AddComment";
import { capitalize } from "@/helpers/capitalize";
import { useLocale, useTranslations } from "next-intl";

export default function Comment({ comment, session, article, isReply = false }) {
    const [replyOpen, setReplyOpen] = useState(false);
    const [replies, setReplies] = useState([]);
    const locale = useLocale();
    const t = useTranslations();

    const addReply = (reply) => {
        let tmpReplies = [...replies, reply];

        // Add new reply
        setReplies(tmpReplies);
        setReplyOpen(false);
    };

    useEffect(() => {
        let tmpReplies = comment.responses;

        setReplies(tmpReplies);
    }, []);

    return (
        <div className="group" id={`c${comment.id}`}>
            {/* Comment card */}
            <div className={`
                relative rounded-xl transition-all duration-200
                ${isReply
                    ? "bg-slate-50/50 dark:bg-bgDark-400/50 p-4"
                    : "hover:bg-slate-50/50 dark:hover:bg-bgDark-400/30 p-4 -ml-4"
                }
            `}>
                {/* Header: Avatar + Name + Date */}
                <div className="flex items-start gap-3">
                    <Link
                        href={`/@${
                            comment.user.pseudo
                                ? comment.user.pseudo
                                : `believer-${comment.user.id}`
                        }`}
                        className="shrink-0"
                    >
                        <div className={`
                            rounded-full overflow-hidden ring-2 ring-white dark:ring-bgDark-300 shadow-sm
                            ${isReply ? "w-8 h-8" : "w-10 h-10"}
                        `}>
                            <Image
                                src={comment.user.photo}
                                alt={`Profile picture`}
                                width={isReply ? 32 : 40}
                                height={isReply ? 32 : 40}
                                className="object-cover"
                            />
                        </div>
                    </Link>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <Link
                                href={`/@${
                                    comment.user.pseudo
                                        ? comment.user.pseudo
                                        : `believer-${comment.user.id}`
                                }`}
                                className="font-semibold text-gray-900 dark:text-white hover:text-primary transition-colors text-sm"
                            >
                                {comment.user.pseudo && comment.user.showPseudo
                                    ? capitalize(comment.user.pseudo)
                                    : `${capitalize(comment.user.firstName)} ${capitalize(comment.user.lastName)}`}
                            </Link>
                            <span className="text-gray-300 dark:text-grayDark-600">·</span>
                            <Link
                                href={`#c${comment.id}`}
                                className="text-xs text-gray-400 dark:text-grayDark-500 hover:text-gray-600 dark:hover:text-grayDark-300 transition-colors"
                            >
                                {moment
                                    .utc(comment.creationDate)
                                    .locale(locale)
                                    .fromNow()}
                            </Link>
                        </div>

                        {/* Content */}
                        <div
                            className="mt-2 text-gray-700 dark:text-grayDark-300 text-[15px] leading-relaxed"
                            dangerouslySetInnerHTML={{
                                __html: comment.content
                                    .replace(/(https?:\/\/\S+?)(?=\s|$)/g, (match) => {
                                        if (match.includes("believemy.com")) {
                                            return `<a href="${match}" target="_blank" class="text-primary hover:underline">${match}</a>`;
                                        } else {
                                            return `<a rel="nofollow" href="${match}" target="_blank" class="text-primary hover:underline">${match}</a>`;
                                        }
                                    })
                                    .replace(/\r?\n/g, "<br />"),
                            }}
                        />

                        {/* Reply button */}
                        {session && (
                            <button
                                onClick={() => setReplyOpen((prev) => !prev)}
                                className={`
                                    mt-3 inline-flex items-center gap-1.5 text-xs font-medium
                                    transition-all duration-200
                                    ${replyOpen
                                        ? "text-primary"
                                        : "text-gray-400 hover:text-primary"
                                    }
                                `}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-3.5 h-3.5"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                                {replyOpen ? t("AddComment_cancel") : t("Comment_reply")}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Reply form */}
            {replyOpen && (
                <div className="relative ml-5 mt-2">
                    {/* Tree connector */}
                    <div className="absolute left-0 top-0 bottom-4 w-px bg-gray-200 dark:bg-grayDark-700" />
                    <div className="absolute left-0 top-6 w-4 h-px bg-gray-200 dark:bg-grayDark-700" />

                    <div className="pl-6">
                        <AddComment
                            article={article}
                            addComment={addReply}
                            setReplyOpen={setReplyOpen}
                            commentId={comment.id}
                            reply
                        />
                    </div>
                </div>
            )}

            {/* Replies */}
            {replies.length > 0 && (
                <div className="relative ml-5 mt-2">
                    {/* Tree connector line */}
                    <div className="absolute left-0 top-0 bottom-4 w-px bg-gray-200 dark:bg-grayDark-700" />

                    <div className="flex flex-col gap-1">
                        {replies.map((reply, index) => (
                            <div key={`r${reply.id}`} className="relative">
                                {/* Horizontal connector */}
                                <div className="absolute left-0 top-7 w-4 h-px bg-gray-200 dark:bg-grayDark-700" />
                                {/* Connector dot */}
                                <div className="absolute left-[14px] top-[26px] w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-grayDark-600" />

                                <div className="pl-6">
                                    <Comment
                                        comment={reply}
                                        session={session}
                                        article={article}
                                        isReply
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
